import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getCategories, getPopularItems, getReviews, getSettings } from '../lib/api';
import { Star, MapPin, Clock, ChevronLeft, ChevronRight, ExternalLink, ArrowRight, UtensilsCrossed, Heart, Phone } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function HomePage() {
  const { t, lang, getLocalizedField, getLocalizedObj } = useLanguage();
  const { addItem } = useCart();
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  const [reviewIdx, setReviewIdx] = useState(0);

  useEffect(() => {
    Promise.all([
      getCategories().then(r => setCategories(r.data)),
      getPopularItems().then(r => setPopular(r.data)),
      getReviews().then(r => setReviews(r.data)),
      getSettings().then(r => setSettings(r.data)),
    ]).catch(console.error);
  }, []);

  const heroTitle = getLocalizedObj(settings.hero_title) || 'Italienische Küche mit Liebe, Stil und Geschmack.';
  const heroSubtitle = getLocalizedObj(settings.hero_subtitle) || 'Willkommen im Ristorante Little Italy in Eberbach.';
  const avgRating = settings.google_rating || 4.7;
  const reviewCount = settings.google_review_count || 186;

  return (
    <div className="min-h-screen">
      {/* ═══ HERO ═══ */}
      <section data-testid="home-hero" className="relative min-h-[78vh] sm:min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1696111677674-0e8a3168b46a?w=1600&q=80" alt="Ristorante Little Italy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(20,17%,8%)]/90 via-[hsl(20,17%,8%)]/70 to-[hsl(20,17%,8%)]/30" />
        </div>
        <div className="relative z-10 mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 w-full py-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(42,72%,44%)]/20 backdrop-blur-sm border border-[hsl(42,72%,44%)]/30 text-[hsl(42,72%,44%)] text-sm font-medium">
                <Star className="w-4 h-4 fill-current" /> {avgRating} Google ({reviewCount}+)
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm">
                <MapPin className="w-3.5 h-3.5" /> Eberbach
              </span>
            </div>
            <h1 className="font-[family-name:var(--font-display)] text-4xl sm:text-5xl lg:text-[3.5rem] tracking-[var(--tracking-display)] text-white mb-5 leading-[1.1]">
              {heroTitle}
            </h1>
            <p className="text-base sm:text-lg text-white/75 mb-10 leading-relaxed max-w-xl font-[family-name:var(--font-body)]">
              {heroSubtitle}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/reservation" data-testid="hero-primary-cta" className="inline-flex items-center px-7 py-3.5 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white shadow-lg hover:brightness-[1.05] transition-colors duration-200">
                {t('hero_cta_reserve')}
              </Link>
              <Link to="/order" data-testid="hero-secondary-cta" className="relative inline-flex flex-col items-center px-7 py-3 rounded-xl text-sm font-semibold bg-white/15 backdrop-blur-sm text-white border border-white/25 hover:bg-white/25 transition-colors duration-200">
                <span>{t('hero_cta_order')}</span>
                <span className="text-[10px] font-medium text-[hsl(42,72%,44%)]">Bis zu 10 % günstiger als Lieferando</span>
              </Link>
              <a href="https://www.lieferando.de/speisekarte/little-italy-eberbach" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-7 py-3.5 rounded-xl text-sm font-semibold bg-[hsl(42,72%,44%)] text-[hsl(20,17%,12%)] hover:brightness-[1.05] transition-colors duration-200">
                Lieferando <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <Link to="/menu" className="inline-flex items-center px-7 py-3.5 rounded-xl text-sm font-semibold bg-white/10 backdrop-blur-sm text-white border border-white/15 hover:bg-white/20 transition-colors duration-200">
                {t('hero_cta_menu')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ GOOGLE REVIEWS BAR ═══ */}
      <section className="bg-white border-b border-[hsl(32,17%,84%)]">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-6 h-6 ${s <= Math.round(avgRating) ? 'fill-[hsl(42,72%,44%)] text-[hsl(42,72%,44%)]' : 'text-[hsl(32,17%,84%)]'}`} />)}</div>
              <div><span className="font-[family-name:var(--font-display)] text-2xl">{avgRating}</span><span className="text-sm text-[hsl(22,10%,38%)] ml-2">{t('based_on')} {reviewCount} {t('reviews_count')}</span></div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-[hsl(32,17%,84%)]" />
            <div className="flex items-center gap-5 text-sm text-[hsl(22,10%,38%)]">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[hsl(16,55%,36%)]" />Alter Markt 6, Eberbach</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[hsl(16,55%,36%)]" />Di-Sa 11:30-22:00</span>
              <a href="tel:01797007958" className="flex items-center gap-1.5 hover:text-[hsl(16,55%,36%)] transition-colors duration-150"><Phone className="w-4 h-4 text-[hsl(16,55%,36%)]" />0179 / 700 79 58</a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ MIT LIEBE ═══ */}
      <section className="py-[clamp(3.25rem,6vw,5.5rem)]">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center max-w-3xl mx-auto">
            <Heart className="w-8 h-8 mx-auto mb-4 text-[hsl(16,55%,36%)]" />
            <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl mb-4">
              {lang === 'en' ? 'Prepared with Love for You' : lang === 'it' ? 'Preparato con Amore per Voi' : 'Mit Liebe für Sie zubereitet'}
            </h2>
            <p className="text-[hsl(22,10%,38%)] leading-relaxed">
              {getLocalizedObj(settings.about_text) || 'Im Ristorante Little Italy verbinden sich italienische Küche, ehrliche Gastfreundschaft und die Liebe zum guten Geschmack zu einem Ort, an dem Genuss zuhause ist.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ CATEGORIES ═══ */}
      <section className="py-[clamp(3.25rem,6vw,5.5rem)] bg-[hsl(36,33%,92%)]">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
            <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl mb-2">{t('our_menu')}</h2>
            <p className="text-[hsl(22,10%,38%)]">{t('all_categories')}</p>
          </motion.div>
          <div data-testid="menu-category-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {categories.map((cat, i) => (
              <motion.div key={cat.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
                <Link to={`/menu?category=${cat.id}`} className="group block rounded-2xl overflow-hidden bg-white border border-[hsl(32,17%,84%)] hover:shadow-[var(--shadow-md)] transition-shadow duration-300">
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img src={cat.image || 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400&q=80'} alt={getLocalizedField(cat, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="font-[family-name:var(--font-display)] text-white text-base sm:text-lg drop-shadow-lg">{getLocalizedField(cat, 'name')}</h3>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-[hsl(22,10%,38%)] italic line-clamp-1">{getLocalizedField(cat, 'tagline') || getLocalizedField(cat, 'description')}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/menu" className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-[hsl(16,55%,36%)] text-[hsl(16,55%,36%)] hover:bg-[hsl(16,55%,36%)] hover:text-white transition-colors duration-200">
              {t('hero_cta_menu')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ POPULAR / BESTSELLER ═══ */}
      {popular.length > 0 && (
        <section className="py-[clamp(3.25rem,6vw,5.5rem)]">
          <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
              <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl mb-2">{t('house_recommendation')}</h2>
              <p className="text-[hsl(22,10%,38%)]">{t('popular_dishes')}</p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {popular.slice(0, 6).map((item, i) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }} className="bg-white rounded-2xl border border-[hsl(32,17%,84%)] overflow-hidden hover:shadow-[var(--shadow-md)] transition-shadow duration-300">
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-[family-name:var(--font-display)] text-lg">{getLocalizedField(item, 'name')}</h3>
                      <span className="text-[hsl(16,55%,36%)] font-bold whitespace-nowrap tabular-nums">{item.price.toFixed(2)} €</span>
                    </div>
                    <p className="text-sm text-[hsl(22,10%,38%)] mb-4 line-clamp-2">{getLocalizedField(item, 'description')}</p>
                    <button onClick={() => addItem({ id: item.id, name: getLocalizedField(item, 'name'), price: item.price, image: item.image })} className="w-full py-2.5 rounded-xl text-sm font-medium border border-[hsl(16,55%,36%)] text-[hsl(16,55%,36%)] hover:bg-[hsl(16,55%,36%)] hover:text-white transition-colors duration-200">
                      {t('add_to_cart')}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ REVIEWS ═══ */}
      {reviews.length > 0 && (
        <section className="py-[clamp(3.25rem,6vw,5.5rem)] bg-[hsl(36,33%,92%)]">
          <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
              <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl mb-2">{t('reviews_title')}</h2>
              <div className="flex items-center justify-center gap-2">
                <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className="w-5 h-5 fill-[hsl(42,72%,44%)] text-[hsl(42,72%,44%)]" />)}</div>
                <span className="text-sm text-[hsl(22,10%,38%)]">{avgRating} — {reviewCount} {t('google_reviews')}</span>
              </div>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.slice(0, 6).map((review, i) => (
                <motion.div key={review.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="bg-white rounded-2xl p-6 border border-[hsl(32,17%,84%)]">
                  <div className="flex gap-0.5 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[hsl(42,72%,44%)] text-[hsl(42,72%,44%)]' : 'text-[hsl(32,17%,84%)]'}`} />)}</div>
                  <p className="text-sm text-[hsl(20,17%,12%)] mb-4 leading-relaxed italic">"{review.text}"</p>
                  <div className="flex items-center justify-between"><span className="font-medium text-sm">{review.author}</span><span className="text-xs text-[hsl(22,10%,38%)]">{review.date}</span></div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8"><Link to="/reviews" className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(16,55%,36%)] hover:underline">{t('reviews_page_title')} <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      )}

      {/* ═══ RESERVATION CTA ═══ */}
      <section className="py-[clamp(3.25rem,6vw,5.5rem)] bg-[hsl(20,17%,12%)] text-white">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8 text-center">
          <UtensilsCrossed className="w-10 h-10 mx-auto mb-4 text-[hsl(42,72%,44%)]" />
          <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl mb-3">{t('reservation_title')}</h2>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">{t('reservation_subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/reservation" className="inline-flex items-center px-8 py-3.5 rounded-xl text-sm font-semibold bg-[hsl(16,55%,36%)] text-white shadow-lg hover:brightness-[1.05] transition-colors duration-200">{t('hero_cta_reserve')}</Link>
            <a href="tel:01797007958" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold border border-white/25 text-white hover:bg-white/10 transition-colors duration-200"><Phone className="w-4 h-4" />0179 / 700 79 58</a>
          </div>
        </div>
      </section>

      {/* ═══ LIEFERANDO ═══ */}
      <section className="py-[clamp(3.25rem,6vw,5.5rem)]">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
          <div data-testid="lieferando-block" className="bg-white rounded-2xl border-2 border-[hsl(97,18%,34%)] p-8 sm:p-12 text-center">
            <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl mb-3">{t('lieferando_text')}</h2>
            <p className="text-[hsl(22,10%,38%)] mb-6 max-w-md mx-auto">{lang === 'en' ? 'Order comfortably via Lieferando and enjoy our dishes at home.' : lang === 'it' ? 'Ordinate comodamente su Lieferando e godetevi i nostri piatti a casa.' : 'Bestellen Sie bequem über Lieferando und genießen Sie unsere Gerichte zu Hause.'}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="https://www.lieferando.de/speisekarte/little-italy-eberbach" target="_blank" rel="noopener noreferrer" data-testid="lieferando-primary-button" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold bg-[hsl(97,18%,34%)] text-white hover:brightness-[1.1] transition-colors duration-200">{t('lieferando_cta')} <ExternalLink className="w-4 h-4" /></a>
              <Link to="/order" data-testid="lieferando-secondary-button" className="inline-flex items-center px-7 py-3.5 rounded-xl text-sm font-semibold border border-[hsl(32,17%,84%)] text-[hsl(20,17%,12%)] hover:bg-[hsl(35,20%,92%)] transition-colors duration-200">{t('hero_cta_order')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ LOCATION & CONTACT ═══ */}
      <section className="py-[clamp(3.25rem,6vw,5.5rem)] bg-[hsl(36,33%,92%)]">
        <div className="mx-auto max-w-[72rem] px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
            <h2 className="font-[family-name:var(--font-display)] text-2xl sm:text-3xl">{t('location_contact')}</h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden border border-[hsl(32,17%,84%)] h-[350px]">
              <iframe src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=place_id:ChIJex41VCrul0cRTN6JEiWXGAg&language=de" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Location" />
            </div>
            <div className="space-y-5">
              <div className="bg-white rounded-2xl p-6 border border-[hsl(32,17%,84%)]">
                <h3 className="font-[family-name:var(--font-display)] text-lg mb-4">{t('visit_us')}</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[hsl(16,55%,36%)] mt-0.5" /><div><p className="font-medium">Ristorante Little Italy</p><p className="text-sm text-[hsl(22,10%,38%)]">Alter Markt 6, 69412 Eberbach</p></div></div>
                  <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-[hsl(16,55%,36%)]" /><a href="tel:01797007958" className="text-sm font-medium hover:text-[hsl(16,55%,36%)] transition-colors duration-150">0179 / 700 79 58</a></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[hsl(32,17%,84%)]" data-testid="opening-hours">
                <h3 className="font-[family-name:var(--font-display)] text-lg mb-4">{t('opening_hours')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-[hsl(32,17%,84%)]/50"><span className="font-medium">{t('monday')}</span><span className="text-[hsl(0,72%,45%)]">{t('closed')}</span></div>
                  <div className="flex justify-between py-1 border-b border-[hsl(32,17%,84%)]/50"><span className="font-medium">{t('tuesday')} – {t('saturday')}</span><span>11:30–14:00 & 17:00–22:00</span></div>
                  <div className="flex justify-between py-1"><span className="font-medium">{t('sunday')}</span><span>11:30–22:00</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
